/**
 * Shared utilities for rate change logic
 * Used across rate shop components for consistent rate change handling
 */

import type { OtaiRate } from '@/graphql/types';
import { dow } from '@/helpers/date-helpers';

/**
 * Determines priority order for rate change selection based on meeting type and day of week
 */
export function getDayPriorityOrder(meetingCategory?: string): number[] {
  if (meetingCategory === 'WEEKLY') {
    return [7, 1, 3]; // Weekly meetings: prioritize 7-day changes first
  }

  const currentDow = dow();
  return currentDow === 'Mon' ? [3, 1, 7] : [1, 3, 7];
}

/**
 * Selects the optimal rate change based on priority order and meeting category
 */
export function selectOptimalRateChange(
  changes: OtaiRate['changes'],
  meetingCategory?: string,
): NonNullable<OtaiRate['changes']>[0] | undefined {
  const safeChanges = Array.isArray(changes) ? changes : [];
  const priorityOrder = getDayPriorityOrder(meetingCategory);

  for (const days of priorityOrder) {
    const change = safeChanges.find((c) => c && c.days === days);
    if (change) {
      return change;
    }
  }

  return safeChanges[0];
}

/**
 * Gets the most recent available rate change (for sold-out replacement)
 * Returns the rate from the most recent day (smallest days value)
 */
export function getMostRecentRateChange(changes: OtaiRate['changes']): number | null {
  if (!Array.isArray(changes) || !changes.length) return null;

  const validChanges = changes
    .filter((change) => change?.change?.value && change.change.value > 0)
    .sort((a, b) => (a?.days || 99) - (b?.days || 99));

  return validChanges[0]?.change?.value || null;
}
